import { useState } from 'react';
import { Download, Database, Trash2, Loader as Loader2, CircleCheck as CheckCircle } from 'lucide-react';
import { useBusiness } from '@/contexts/BusinessContext';
import { exportPaymentsCsv, exportExpensesCsv, exportCustomersCsv, exportAllDataJson } from '@/lib/api';

function downloadFile(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function DataManagementSection() {
  const { selectedBusiness } = useBusiness();
  const [exporting, setExporting] = useState(false);
  const [backing, setBacking] = useState(false);
  const [cleared, setCleared] = useState(false);

  async function handleExportCsv() {
    if (!selectedBusiness) return;
    setExporting(true);
    try {
      const [payments, expenses, customers] = await Promise.all([
        exportPaymentsCsv(selectedBusiness.id),
        exportExpensesCsv(selectedBusiness.id),
        exportCustomersCsv(selectedBusiness.id),
      ]);

      downloadFile(payments, `payments_${selectedBusiness.name}.csv`, 'text/csv');
      setTimeout(() => downloadFile(expenses, `expenses_${selectedBusiness.name}.csv`, 'text/csv'), 300);
      setTimeout(() => downloadFile(customers, `customers_${selectedBusiness.name}.csv`, 'text/csv'), 600);
    } catch (err: any) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  async function handleBackup() {
    if (!selectedBusiness) return;
    setBacking(true);
    try {
      const json = await exportAllDataJson(selectedBusiness.id);
      downloadFile(json, `backup_${selectedBusiness.name}_${new Date().toISOString().slice(0, 10)}.json`, 'application/json');
    } catch (err: any) {
      alert(err.message || 'Backup failed');
    } finally {
      setBacking(false);
    }
  }

  function handleClearCache() {
    localStorage.clear();
    setCleared(true);
    setTimeout(() => setCleared(false), 2500);
  }

  const actions = [
    {
      icon: Download,
      label: 'Export All Data',
      desc: 'Download payments, expenses, customers as CSV',
      onClick: handleExportCsv,
      loading: exporting,
      color: 'teal',
    },
    {
      icon: Database,
      label: 'Backup Data',
      desc: 'Download a full JSON backup file',
      onClick: handleBackup,
      loading: backing,
      color: 'teal',
    },
    {
      icon: cleared ? CheckCircle : Trash2,
      label: cleared ? 'Cache Cleared' : 'Clear Cache',
      desc: 'Remove locally stored data from browser',
      onClick: handleClearCache,
      loading: false,
      color: cleared ? 'green' : 'slate',
    },
  ];

  return (
    <div>
      <h2 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2.5 px-1">Data Management</h2>
      <div className="glass-panel rounded-2xl p-2">
        {actions.map((a, idx) => (
          <div key={a.label}>
            {idx > 0 && <div className="h-px bg-white/10 mx-3 my-1" />}
            <button
              onClick={a.onClick}
              disabled={a.loading}
              className="w-full flex items-center gap-3 py-2.5 glass-hover rounded-xl px-3"
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${a.color === 'green' ? 'bg-green-500/10' : a.color === 'teal' ? 'bg-teal-500/10' : 'bg-white/10'}`}>
                {a.loading ? (
                  <Loader2 size={15} className="text-teal-400 animate-spin" />
                ) : (
                  <a.icon size={15} className={a.color === 'green' ? 'text-green-800' : a.color === 'teal' ? 'text-teal-400' : 'text-white'} />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold text-white">{a.label}</p>
                <p className="text-[11px] text-white">{a.desc}</p>
              </div>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
